import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';

import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  EsgRecord,
  EsgRecordUpdateResponse,
  RecordGroup,
  RecordGroupBy,
  RecordListQuery
} from '../../../types/syntiro_honey';
import RecordsGrid from './RecordsGrid';
import CompletenessMeter from './CompletenessMeter';
import { ReviewFilters, useRecordList } from './useRecordList';
import { useRecordGroups } from './useRecordGroups';
import { toSortQuery } from './record_sort';

/** The first-page state {@link RecordGroupRows} starts on and resets to. */
const INITIAL_PAGINATION_MODEL: GridPaginationModel = { page: 0, pageSize: 25 };

/** Translates a group key into the list filter that scopes a fetch to that group. */
function groupFilter(groupBy: RecordGroupBy, key: string | null): Partial<RecordListQuery> {
  // null = the "none" bucket; the backend selects it with the `none` sentinel.
  const value = key ?? 'none';
  if (groupBy === 'metric') return { metricId: value };
  if (groupBy === 'division') return { divisionId: value };
  return { periodId: value };
}

function isGroupFullyApproved(group: RecordGroup): boolean {
  return group.exact + group.suggested > 0 && group.approved >= group.exact + group.suggested;
}

interface GroupCountChipsProps {
  /** The group whose mapping-trust breakdown to render. */
  group: RecordGroup;
  /**
   * Whether the list is scoped to approved records only. On that scope an
   * outstanding-work chip is a contradiction, so the breakdown is suppressed.
   */
  approvedScope?: boolean;
}

/** The per-group status roll-up: only the non-zero buckets, in triage order. */
function GroupCountChips({ group, approvedScope = false }: GroupCountChipsProps) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {!approvedScope && group.exact > 0 && (
        <Chip size="small" color="success" label={`${group.exact} exact`} />
      )}
      {!approvedScope && group.suggested > 0 && (
        <Chip size="small" color="warning" label={`${group.suggested} to confirm`} />
      )}
      {group.unmapped > 0 && <Chip size="small" label={`${group.unmapped} unmapped`} />}
      {/* Duplication is orthogonal to the mapping-trust buckets above: a duplicated
          row is usually EXACT, so without this chip a group of duplicates reads as
          all-green "approve me" (Maxine R5 ③). */}
      {group.duplicates > 0 && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`${group.duplicates} duplicate${group.duplicates === 1 ? '' : 's'}`}
        />
      )}
    </Stack>
  );
}

interface RecordGroupRowsProps {
  /** The grouping dimension (so the scoping filter targets the right field). */
  groupBy: RecordGroupBy;
  /** This group's key (null for the "none" bucket). */
  groupKey: string | null;
  /** The active review filters, merged with the group-scoping filter. */
  filters: ReviewFilters;
  /** Shared sort model (sorting applies across all groups). */
  sortModel: GridSortModel;
  /** Shared sort change handler. */
  onSortModelChange: (model: GridSortModel) => void;
  /** Shared row selection (spans groups for bulk actions). */
  selection: GridRowSelectionModel;
  /** Selection change handler. */
  onSelectionChange: (selection: GridRowSelectionModel) => void;
  /** Open a record's detail modal, passing this group's loaded ids for Prev/Next. */
  onOpenRecord: (record: EsgRecord, siblingIds: Array<string>) => void;
  /** Forwarded to the nested grid: raises the "apply to the rows sharing this label" offer. */
  onMappingConfirmed?: (updated: EsgRecordUpdateResponse) => void;
  /** Viewer mode: forwarded to the nested grid (no selection/confirm affordances). */
  readOnly?: boolean;
}

/**
 * The rows of a single expanded group: a server-paginated {@link RecordsGrid}
 * scoped to the group. Pagination is local (each group paginates independently);
 * sort and selection are shared with the parent so sorting is consistent and bulk
 * selection spans groups. Only mounted when its accordion is expanded (lazy fetch).
 * @returns {JSX.Element}
 */
function RecordGroupRows({
  groupBy,
  groupKey,
  filters,
  sortModel,
  onSortModelChange,
  selection,
  onSelectionChange,
  onOpenRecord,
  onMappingConfirmed,
  readOnly = false
}: RecordGroupRowsProps) {
  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(INITIAL_PAGINATION_MODEL);

  // The group's record set is determined by groupBy/groupKey (which records the
  // scoping filter selects), filters (the shared review filters), and sortModel
  // (server-side sort, which can shift which rows fall on which page). A stale
  // page held across one of these changing can point past the group's new row
  // count — e.g. paging to page 3 of 300 rows, then a filter narrows the group to
  // 12 rows, still requesting offset 75 and rendering "no rows" despite the
  // group's own header chip correctly showing 12. Mirrors ReviewPanel's own
  // filter/sort pagination reset (ReviewPanel.tsx), keeping pageSize so a
  // reviewer's chosen rows-per-page survives an unrelated filter change.
  // Excluding paginationModel itself from the deps avoids this effect re-firing
  // on its own reset.
  useEffect(() => {
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }, [groupBy, groupKey, filters, sortModel]);

  const query = useMemo<RecordListQuery>(
    () => ({
      ...filters,
      ...groupFilter(groupBy, groupKey),
      ...toSortQuery(sortModel),
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize
    }),
    [filters, groupBy, groupKey, sortModel, paginationModel]
  );
  const { result, loading, refresh } = useRecordList(query);
  const pageIds = (result?.items ?? []).map((item) => item.id);

  return (
    <RecordsGrid
      records={result?.items ?? []}
      total={result?.total ?? 0}
      loading={loading}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      selection={selection}
      onSelectionChange={onSelectionChange}
      onRowOpen={(record: EsgRecord) => onOpenRecord(record, pageIds)}
      onRecordMutated={() => void refresh()}
      onMappingConfirmed={onMappingConfirmed}
      viewedFileId={filters.fileId}
      readOnly={readOnly}
    />
  );
}

interface RecordGroupListProps {
  /** Viewer mode: hide group-approve and selection affordances. */
  readOnly?: boolean;
  /** The grouping dimension. */
  groupBy: RecordGroupBy;
  /** The active review filters. */
  filters: ReviewFilters;
  /** Shared sort model. */
  sortModel: GridSortModel;
  /** Shared sort change handler. */
  onSortModelChange: (model: GridSortModel) => void;
  /** Shared row selection (spans groups). */
  selection: GridRowSelectionModel;
  /** Selection change handler. */
  onSelectionChange: (selection: GridRowSelectionModel) => void;
  /** Open a record's detail modal with its group's loaded ids for Prev/Next. */
  onOpenRecord: (record: EsgRecord, siblingIds: Array<string>) => void;
  /** Raises the "apply to the rows sharing this label" offer after an inline confirm. */
  onMappingConfirmed?: (updated: EsgRecordUpdateResponse) => void;
  /** Approve every record in a group, given the group-scoped list query. */
  onApproveGroup: (groupQuery: RecordListQuery) => Promise<void>;
  /** Whether a bulk action is in flight (disables the per-group approve buttons). */
  busy: boolean;
  /** Bump to refetch the groups + remount expanded rows after a mutation/refresh. */
  refreshKey: number;
}

/** A stable map key for the null ("none") bucket, used for the approving-spinner state. */
const NONE_GROUP_KEY = '__none__';

/**
 * The collapsible metric/entity/period view of the review list: one accordion per
 * group, each header showing its record count and mapping-trust roll-up, expanding
 * to a group-scoped records grid (lazily fetched). Selection and sort are shared so
 * bulk actions span groups; pagination is per-group.
 * @returns {JSX.Element}
 */
export default function RecordGroupList({
  groupBy,
  filters,
  sortModel,
  onSortModelChange,
  selection,
  onSelectionChange,
  onOpenRecord,
  onMappingConfirmed,
  onApproveGroup,
  busy,
  refreshKey,
  readOnly = false
}: RecordGroupListProps) {
  const groupsQuery = useMemo(() => ({ ...filters, groupBy }), [filters, groupBy]);
  const { groups, problem } = useRecordGroups(groupsQuery, refreshKey);
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  // A group awaiting the duplicate warning before its approval runs (Maxine R5 ③):
  // one click straight onto "Approve group" must not silently approve both copies
  // of a cross-file duplicate, so groups carrying duplicates confirm first.
  const [duplicateWarnGroup, setDuplicateWarnGroup] = useState<RecordGroup | null>(null);

  const handleApproveGroup = async (group: RecordGroup): Promise<void> => {
    setApprovingKey(group.key ?? NONE_GROUP_KEY);
    try {
      await onApproveGroup({ ...filters, ...groupFilter(groupBy, group.key) });
    } finally {
      setApprovingKey(null);
    }
  };

  const handleApproveClick = (group: RecordGroup): void => {
    if (group.duplicates > 0) {
      setDuplicateWarnGroup(group);
      return;
    }
    void handleApproveGroup(group);
  };

  if (problem !== null) {
    return (
      <Alert severity="error">{problem.detail ?? problem.title ?? 'Could not load groups.'}</Alert>
    );
  }
  if (groups === null) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (groups.length === 0) {
    return <Alert severity="info">No records match the current filters.</Alert>;
  }

  return (
    <Stack spacing={1}>
      {groups.map((group) => (
        <Accordion
          key={group.key ?? NONE_GROUP_KEY}
          disableGutters
          variant="outlined"
          // Mount the rows only when expanded so each group is fetched lazily; remount
          // on refreshKey so a mutation elsewhere refetches the open group.
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%', pr: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="baseline">
                <BodyText weight="Bold">{group.label}</BodyText>
                <CaptionText>
                  {group.total} record{group.total === 1 ? '' : 's'}
                </CaptionText>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Per-group completeness: how much of this metric is approved,
                    shown for every group so a done metric reads at a glance. */}
                <CompletenessMeter
                  variant="compact"
                  approved={group.approved}
                  total={group.total}
                />
                <GroupCountChips
                  group={group}
                  approvedScope={filters.status === 'APPROVED' || isGroupFullyApproved(group)}
                />
                {/* The wrapper stops the click bubbling to the summary so approving
                    a group does not also toggle its accordion. */}
                <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'inline-flex' }}>
                  {/* Only mapped records are approvable; once none remain unapproved
                      the action is replaced with a done label so the group no longer
                      offers a confusing no-op button (Maxine #13). The label says
                      "all MAPPED approved" because its denominator is exact+suggested,
                      not the group total the meter beside it reports. */}
                  {isGroupFullyApproved(group) ? (
                    <Chip size="small" color="success" label="All mapped approved" />
                  ) : (
                    !readOnly && (
                      <PrimaryButton
                        palette_color="success"
                        disabled={busy || group.exact + group.suggested === 0}
                        loading={approvingKey === (group.key ?? NONE_GROUP_KEY)}
                        onClick={() => handleApproveClick(group)}
                      >
                        Approve group
                      </PrimaryButton>
                    )
                  )}
                </Box>
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <RecordGroupRows
              key={refreshKey}
              groupBy={groupBy}
              groupKey={group.key}
              filters={filters}
              sortModel={sortModel}
              onSortModelChange={onSortModelChange}
              selection={selection}
              onSelectionChange={onSelectionChange}
              onOpenRecord={onOpenRecord}
              onMappingConfirmed={onMappingConfirmed}
              readOnly={readOnly}
            />
          </AccordionDetails>
        </Accordion>
      ))}
      <ConfirmationModal
        open={duplicateWarnGroup !== null}
        title="This group contains duplicates"
        description={
          duplicateWarnGroup === null
            ? ''
            : `${duplicateWarnGroup.duplicates} record${
                duplicateWarnGroup.duplicates === 1 ? ' is' : 's are'
              } already reported in another file. If you approve both copies, the data counts twice. Open the flagged records to decide which copy to keep.`
        }
        confirm_button_text="Approve anyway"
        confirm_loading={busy}
        handleCloseModal={() => setDuplicateWarnGroup(null)}
        // Returning the promise lets the modal await completion before closing.
        handleConfirm={async () => {
          if (duplicateWarnGroup !== null) {
            await handleApproveGroup(duplicateWarnGroup);
          }
        }}
      />
    </Stack>
  );
}
